/**
 * LinkComponent is an Angular component that enhances anchor (`<a>`) elements with customizable button-like styles and optional icons.
 * 
 * ## Usage Examples
 * 
 * ```html
 * <!-- Default link -->
 * <a vee-link right icon="angleClosed" [class]="color">Test</a>
 * 
 * <!-- Underlined style -->
 * <a vee-link="underlined" right icon="angleClosed" [class]="color">Test</a>
 * 
 * <!-- Filled style -->
 * <a vee-link="filled" right icon="angleClosed" [class]="color">Test</a>
 * 
 * <!-- Active state -->
 * <a vee-link class="active" right icon="angleClosed" [class]="color">Test</a>
 * 
 * <!-- Disabled state -->
 * <a vee-link disabled right icon="angleClosed" [class]="color">Test</a>
 * ```
 * 
 * ## Inputs
 * - `vee-link`: Sets the button style (`'' | 'filled' | 'outlined' | 'underlined'`).
 * - `icon`: Name of the icon to display (from `usableIcons`).
 * - `disabled`: Disables the link when true.
 * - `right`: Positions the icon to the right.
 * - `left`: Positions the icon to the left.
 * 
 * ## Host Bindings
 * Applies CSS classes based on the selected style, icon position, and disabled state.
 */
import { booleanAttribute, Component, HostBinding, Input } from '@angular/core';
import { iconWeight, usableIcons } from '../icon/icon.component';
import { btnType } from './button.component';

@Component({
  selector: 'a [vee-link]',
  templateUrl: './clickable.component.html',
  styleUrls: ['./clickable.component.scss'],
  standalone: false,
})
export class LinkComponent {
  @Input('vee-link') veeBtn?: btnType;
  @Input('icon') icon?: usableIcons;
    @Input('icon-weight') iconWeight: iconWeight = 'regular';
  @Input({ transform: booleanAttribute }) disabled: boolean = false;
  @Input({ transform: booleanAttribute }) right: boolean = true;
  @Input({ transform: booleanAttribute }) left: boolean = false;

  @HostBinding('class')
  get hostClasses(): string {
    let iconClass = '';
    if (this.icon && this.left) {
      iconClass = 'vee-icon-left';
    } else if (this.icon && this.right) {
      iconClass = 'vee-icon-right';
    }
    return [
      this.veeBtn? `vee-link-${this.veeBtn}` : 'vee-link-text',
      iconClass,
      this.disabled ? 'disabled' : '',
    ].join(' ');
  }
}
